<script setup lang="ts">
  import { ref, watchEffect, toRefs, useAttrs, computed } from 'vue'
  import List from './List.vue'
  import Footer from './Footer.vue'
  const attrs = useAttrs()
  const openMultiSelect = ref(false)
  const searchValue = ref('')
  const itemsList = ref()
  const arraySelected = ref<any[]>([])
  const count = ref(0)
  const selected = ref()
  const copyItems = ref()
  const copySelected = ref()
  const countCopy = ref()

  const props = withDefaults(
    defineProps<{
      label: string
      modelValue: any[]
      options: any[]
      headerTitle: string
      showKey: string
      valueKey: string
      error?: string
      disabled: boolean
      required: boolean
      customClass?: string
      field?: string
      readonly?: string
      allModel?: string
      //openMultiSelect: boolean
      emitObjectValue?: boolean
    }>(),
    {
      label: '',
      modelValue: () => [],
      options: () => [],
      headerTitle: '',
      showKey: 'name',
      valueKey: 'id',
      error: '',
      disabled: false,
      required: false,
      customClass: '',
      //openMultiSelect: false
      emitObjectValue: false,
    }
  )

  const { modelValue } = toRefs(props)

  const shouldEmitObjectValue = computed(() => props.emitObjectValue)

  const ensureComparable = (value: any) => {
    if (value === undefined || value === null) {
      return value
    }
    if (typeof value === 'object') {
      return JSON.stringify(value)
    }
    return typeof value === 'number' ? value.toString() : value
  }

  const getIdentifier = (item: any) => {
    if (!props.valueKey) {
      return ensureComparable(item)
    }

    return ensureComparable(item?.[props.valueKey])
  }

  const getModelIdentifiers = () => {
    if (!Array.isArray(props.modelValue)) {
      return new Set()
    }

    if (shouldEmitObjectValue.value) {
      return new Set(
        props.modelValue.map((value: any) => {
          if (props.valueKey) {
            return ensureComparable(value?.[props.valueKey])
          }
          return ensureComparable(value)
        })
      )
    }

    return new Set(
      props.modelValue.map((value: any) => ensureComparable(value))
    )
  }

  watchEffect(() => {
    itemsList.value = props.options
  })

  const emits = defineEmits(['update:modelValue', 'change', 'filter', 'file'])

  const emitUpdate = (val: any) => {
    emits('update:modelValue', val)
  }

  const emitChange = () => {
    emits('change')
  }

  const addSelected = (item: any) => {
    arraySelected.value.push(item)
    itemsList.value = filter(itemsList.value, arraySelected.value)
    count.value += 1
  }

  const deleteSelected = (item: any) => {
    itemsList.value.push(item)
    arraySelected.value = filter(arraySelected.value, itemsList.value)
    count.value -= 1
  }

  const filter = (items: any[], listFilter: any[]) => {
    const identifiers = new Set(listFilter.map((item) => getIdentifier(item)))
    return items.filter((item) => !identifiers.has(getIdentifier(item)))
  }

  const openSelect = () => {
    if (props.options?.length > 0) openMultiSelect.value = true
    copyItems.value = [...itemsList.value]
    copySelected.value = [...arraySelected.value]
    countCopy.value = count.value
    if (
      Array.isArray(props.modelValue) &&
      props.modelValue.length === props.options.length
    ) {
      checkAll()
    } else {
      const selectedIdentifiers = getModelIdentifiers()

      const itemSelected = itemsList.value.filter((item: any) =>
        selectedIdentifiers.has(getIdentifier(item))
      )
      for (const item of itemSelected) {
        addSelected(item)
      }
    }
  }

  const checkAll = () => {
    arraySelected.value = []
    arraySelected.value = [...props.options]
    itemsList.value = []
    count.value = arraySelected.value?.length
  }

  const uncheckAll = () => {
    arraySelected.value = []
    itemsList.value = props.options
    count.value = 0
  }

  const saveSelection = () => {
    selected.value = arraySelected.value
    //emitUpdate(selected.value.map((_) => _[props.valueKey].toString()))
    if (shouldEmitObjectValue.value) {
      emitUpdate(selected.value.map((item: any) => ({ ...item })))
    } else {
      emitUpdate(
        selected.value.map((_: any) =>
          typeof _[props.valueKey] == 'number'
            ? _[props.valueKey].toString()
            : _[props.valueKey]
        )
      )
    }
    emitChange()
    openMultiSelect.value = false
  }

  const cancelSelection = () => {
    itemsList.value = copyItems.value
    arraySelected.value = copySelected.value
    openMultiSelect.value = false
    count.value = arraySelected.value?.length
  }
</script>
<template>
  <div class="w-full" :class="customClass" :id="attrs.id as string">
    <!-- <label class="block text-left text-sm font-medium text-gray-700 mb-1.5">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label> -->
    <label
      class="block w-full py-2 px-2 border-radius-sm text-base text-color surface-overlay p-2 border-1 border-solid surface-border border-round appearance-none outline-none focus:border-primary border"
      :class="[error === '' ? 'border-gray-300' : 'border-red-500']"
    >
      <div
        class="w-full h-full align-items-center justify-content-center flex"
        v-if="options?.length > 0"
      >
        <span class="text-overflow-ellipsis">
          {{
            `${
              Array.isArray(modelValue) ? modelValue.length : 0
            } Opciones Seleccionadas`
          }}
        </span>
      </div>

      <div
        class="w-full h-full align-items-center justify-content-between flex"
        v-else
      >
        <span class="text-gray-400 pl-2">
          {{ `Esperando datos...` }}
        </span>

        <i class="pi pi-spin pi-spinner" style="font-size: 1rem"></i>
      </div>

      <input
        class="opacity-0 hidden"
        @click="openSelect"
        @input="($event: any) => emitUpdate($event.target.value)"
        :value="modelValue"
      />
    </label>
    <span class="text-xs text-red-500">{{ error }}</span>
  </div>
  <Dialog
    v-model:visible="openMultiSelect"
    :style="{ width: '50vw', maxheight: '82vh' }"
    :showHeader="false"
    modal
    :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
    class="border-round-sm"
  >
    <Card class="pt-3 px-3 border-noround shadow-none min-h-82vh">
      <template #header>
        <header
          class="sticky top-0 background-gray-100 w-full h-16 px-4 flex align-items-center justify-content-center gap-3 border"
        >
          <!-- <slot name="title_header"></slot> -->
          <h1 class="font-bold text-xl">{{ headerTitle }}</h1>
          <div
            class="flex justify-content-center align-items-center border-circle bg-blue-600 text-white text-900 text-xl h-2rem w-2rem"
          >
            {{ count }}
          </div>
        </header>
      </template>
      <template #content>
        <article>
          <div class="p-3">
            <InputText
              placeholder="Buscar..."
              v-model="searchValue"
            ></InputText>
          </div>

          <div class="flex flex-row items-start">
            <div
              class="w-screen py-5 px-2 max-h-22rem overflow-y-auto overflow-x-hidden"
            >
              <a
                :class="[
                  { 'cursor-not-allowed pointer-events-none': disabled },
                ]"
                class="text-gray-400 cursor-pointer underline"
                @click.prevent="checkAll"
              >
                Marcar Todos
              </a>
              <List
                v-for="(item, index) in itemsList.filter(function (x: any) {
                  return (
                    searchValue == '' ||
                    x[showKey]
                      .toUpperCase()
                      .indexOf(searchValue.toUpperCase()) > -1
                  )
                })"
                :key="index"
                :nameItem="item[showKey]"
                @clicked="addSelected(item)"
                :class="[
                  { 'cursor-not-allowed pointer-events-none': disabled },
                ]"
              >
              </List>
            </div>
            <div
              class="w-screen py-5 px-2 max-h-22rem overflow-y-auto overflow-x-hidden"
            >
              <a
                class="text-gray-400 cursor-pointer underline"
                :class="[
                  { 'cursor-not-allowed pointer-events-none': disabled },
                ]"
                @click.prevent="uncheckAll"
              >
                Desmarcar Todos
              </a>
              <List
                v-for="(item, index) in arraySelected.filter(function (x: any) {
                  return (
                    searchValue == '' ||
                    x[showKey]
                      .toUpperCase()
                      .indexOf(searchValue.toUpperCase()) > -1
                  )
                })"
                :key="index"
                :nameItem="item[showKey]"
                @clicked="deleteSelected(item)"
                :class="[
                  { 'cursor-not-allowed pointer-events-none': disabled },
                ]"
              >
              </List>
            </div>
          </div>
        </article>
      </template>
      <template #footer>
        <Footer
          style="margin: 0px !important; padding: 0px !important"
          class="sticky w-full flex justify-content-center align-items-end sm:justify-content-end h-4rem gap-2"
        >
          <Button severity="secondary" class="mr-2" @click="cancelSelection()"
            >Cancelar</Button
          >
          <Button @click.prevent="saveSelection">Aceptar</Button>
        </Footer>
      </template>
    </Card>
  </Dialog>
</template>
<style>
  .card-container {
    width: 100vw;
    max-width: 750px;
  }

  .h-9_3 {
    height: 2.375rem;
  }
</style>
